import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLines } from "./Reveal";
import { usePerspective } from "@/context/PerspectiveContext";
import { getContent } from "@/content/perspectives";

const HERO_IMG = "https://images.unsplash.com/photo-1527576539890-dfa815648363";

const Hero = () => {
  const ref = useRef(null);
  const { perspective } = usePerspective();
  const { hero } = getContent(perspective);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  const scrollDown = () =>
    document.getElementById("intro")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden bg-ink"
    >
      {/* Parallax background */}
      <motion.div className="absolute inset-0 z-0" style={{ y: imgY, scale: 1.15 }}>
        <img
          src={hero.img || HERO_IMG}
          alt={hero.alt || "Architectural facade"}
          className="h-full w-full object-cover opacity-80"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 z-10 grain"
        style={{ opacity: overlayOpacity }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-cobalt-deep/60" />
        <div className="absolute inset-0 bg-cobalt-dark/30 mix-blend-multiply" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-20 mx-auto w-full max-w-[1500px] px-6 pb-20 pt-40 lg:px-12 lg:pb-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-cobalt" />
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cobalt-200 text-white/80">
            AI Business Optimization Firm
          </span>
        </motion.div>

        <h1 className={`font-serif font-medium leading-[0.92] tracking-tight text-white ${hero.headlineClass}`}>
          {hero.lines.map((l, i) => (
            <MaskLines key={l.text} lines={[l.text]} delay={0.5 + i * 0.12} lineClassName={l.className || ""} />
          ))}
        </h1>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 lg:col-start-1"
          >
            {hero.support && (
              <p data-testid="hero-support" className="mb-4 font-serif text-xl italic text-white/90">
                {hero.support}
              </p>
            )}
            <p className="max-w-xl text-lg font-light leading-relaxed text-white/75">{hero.body}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-end lg:col-span-5 lg:col-start-8 lg:justify-end"
          >
            <div className={hero.secondaryCta ? "flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4" : "flex flex-wrap items-center gap-4"}>
              <button
                data-testid="hero-cta"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="group flex items-center justify-center gap-3 bg-white px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-ink transition-all duration-300 hover:bg-cobalt hover:text-white"
              >
                {hero.primaryCta}
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
              {hero.secondaryCta && (
                <button
                  data-testid="hero-secondary-cta"
                  onClick={() => document.getElementById(hero.secondaryCta.target)?.scrollIntoView({ behavior: "smooth" })}
                  className="border border-white/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-white hover:text-ink"
                >
                  {hero.secondaryCta.label}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 z-20 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 lg:right-12 lg:flex"
        data-testid="hero-scroll-down"
      >
        Scroll
        <ArrowDown size={14} className="animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
